/**
 * yanamas Avatar - Personality Visual Adaptations
 * Phase 4: yanamas' appearance changes based on theme/mood
 */

import React from 'react'
import { useTheme } from '@/components/providers/ThemeProvider'

type HortonMood = 'happy' | 'encouraging' | 'thinking' | 'celebrating' | 'sleeping' | 'working' | 'proud'
type HortonSize = 'sm' | 'md' | 'lg' | 'xl'
type HortonStyle = 'emoji' | 'sketch' | 'sticker' | 'stamp'

interface HortonAvatarProps {
  mood?: HortonMood
  size?: HortonSize
  style?: HortonStyle
  message?: string
  animated?: boolean
  className?: string
}

// Mood-based emoji representations
const moodEmojis: Record<HortonMood, string> = {
  happy: '🐘',
  encouraging: '💪🐘',
  thinking: '🤔',
  celebrating: '🎉🐘',
  sleeping: '😴',
  working: '📝',
  proud: '⭐🐘',
}

// Mood-based colors
const moodColors: Record<HortonMood, { bg: string; accent: string }> = {
  happy: { bg: 'var(--washi-tape-blue)', accent: 'var(--horton-blue)' },
  encouraging: { bg: 'var(--washi-tape-green)', accent: 'var(--semantic-success)' },
  thinking: { bg: 'var(--highlight-yellow)', accent: 'var(--horton-warm)' },
  celebrating: { bg: 'var(--washi-tape-pink)', accent: '#9B4A8C' },
  sleeping: { bg: 'var(--paper-margin)', accent: 'var(--ink-faded)' },
  working: { bg: 'var(--highlight-yellow)', accent: 'var(--accent-gold)' },
  proud: { bg: 'var(--washi-tape-pink)', accent: 'var(--bookmark-red)' },
}

// Size configurations
const sizes: Record<HortonSize, { container: number; emoji: number; font: string }> = {
  sm: { container: 40, emoji: 24, font: '10px' },
  md: { container: 60, emoji: 36, font: '12px' },
  lg: { container: 80, emoji: 48, font: '14px' },
  xl: { container: 120, emoji: 72, font: '16px' },
}

export const HortonAvatar: React.FC<HortonAvatarProps> = ({
  mood = 'happy',
  size = 'md',
  style = 'emoji',
  message,
  animated = true,
  className = '',
}) => {
  const { isDiary, diaryThemeConfig } = useTheme()
  const sizeConfig = sizes[size]
  const moodColor = moodColors[mood]

  // Get theme accent color if available
  const accentColor = diaryThemeConfig?.light?.accentPrimary || moodColor.accent
  const bgColor = diaryThemeConfig?.light?.paperSecondary || moodColor.bg

  // Animation classes
  const animationClass = animated ? {
    happy: 'animate-bounce-gentle',
    encouraging: 'animate-pulse-slow',
    thinking: 'animate-sway',
    celebrating: 'animate-bounce',
    sleeping: 'animate-breathe',
    working: '',
    proud: 'animate-glow',
  }[mood] : ''

  // Render based on style
  const renderHorton = () => {
    switch (style) {
      case 'sketch':
        return (
          <svg 
            viewBox="0 0 100 100" 
            width={sizeConfig.emoji} 
            height={sizeConfig.emoji}
            className={animationClass}
          >
            {/* Simple sketch-style elephant */}
            <ellipse cx="50" cy="55" rx="35" ry="30" fill="none" stroke={accentColor} strokeWidth="3" strokeLinecap="round" />
            <path d="M20 50 Q10 60 15 75 Q20 85 25 80" fill="none" stroke={accentColor} strokeWidth="3" strokeLinecap="round" />
            <circle cx="35" cy="45" r="4" fill={accentColor} />
            <ellipse cx="65" cy="55" rx="15" ry="20" fill="none" stroke={accentColor} strokeWidth="2" />
            <path d="M30 70 Q35 80 30 90" fill="none" stroke={accentColor} strokeWidth="2" strokeLinecap="round" />
            <path d="M50 70 Q55 80 50 90" fill="none" stroke={accentColor} strokeWidth="2" strokeLinecap="round" />
          </svg>
        )

      case 'sticker':
        return (
          <div 
            className={`flex items-center justify-center rounded-full ${animationClass}`}
            style={{
              width: sizeConfig.emoji,
              height: sizeConfig.emoji,
              background: `linear-gradient(135deg, ${bgColor} 0%, ${moodColor.bg} 100%)`,
              boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.15)',
              border: `2px solid ${accentColor}`,
            }}
          >
            <span style={{ fontSize: sizeConfig.emoji * 0.6 }}>{moodEmojis[mood]}</span>
          </div>
        )

      case 'stamp':
        return (
          <div 
            className={`flex items-center justify-center ${animationClass}`}
            style={{
              width: sizeConfig.emoji,
              height: sizeConfig.emoji,
              border: `3px solid ${accentColor}`,
              borderRadius: '4px',
              transform: 'rotate(-5deg)',
              opacity: 0.9,
            }}
          >
            <span style={{ fontSize: sizeConfig.emoji * 0.5 }}>{moodEmojis[mood]}</span>
          </div>
        )

      default: // emoji
        return (
          <span 
            className={animationClass}
            style={{ 
              fontSize: sizeConfig.emoji,
              filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.1))',
            }}
          >
            {moodEmojis[mood]}
          </span>
        )
    }
  }

  if (!isDiary) {
    // Simplified version for non-diary mode
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span style={{ fontSize: sizeConfig.emoji * 0.8 }}>🐘</span>
        {message && <span className="text-sm text-secondary">{message}</span>}
      </div>
    )
  }

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`}>
      <div 
        className="relative flex items-center justify-center rounded-full"
        style={{
          width: sizeConfig.container,
          height: sizeConfig.container,
          background: style !== 'emoji' ? 'transparent' : bgColor,
        }}
      >
        {renderHorton()}
      </div>
      
      {message && (
        <div 
          className="text-center px-3 py-1 rounded-lg max-w-xs"
          style={{
            background: bgColor,
            fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
            fontSize: sizeConfig.font,
            color: diaryThemeConfig?.light?.inkSecondary || 'var(--ink-sepia)',
          }}
        >
          {message}
        </div>
      )}
    </div>
  )
}

// yanamas with speech bubble for longer messages
interface HortonWithBubbleProps extends HortonAvatarProps {
  bubblePosition?: 'top' | 'right' | 'bottom' | 'left'
}

export const HortonWithBubble: React.FC<HortonWithBubbleProps> = ({
  message,
  bubblePosition = 'right',
  ...avatarProps
}) => {
  const { diaryThemeConfig } = useTheme()

  if (!message) {
    return <HortonAvatar {...avatarProps} />
  }

  const bgColor = diaryThemeConfig?.light?.paperSecondary || 'var(--washi-tape-blue)'
  const textColor = diaryThemeConfig?.light?.inkPrimary || 'var(--ink-black)'

  const bubbleStyle: React.CSSProperties = {
    background: bgColor,
    fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
    fontSize: '14px',
    color: textColor,
    padding: '12px 16px',
    borderRadius: '12px',
    maxWidth: '250px',
    boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.1)',
    position: 'relative' as const,
  }

  const isHorizontal = bubblePosition === 'left' || bubblePosition === 'right'

  return (
    <div 
      className={`flex items-center gap-3 ${
        bubblePosition === 'left' ? 'flex-row-reverse' : ''
      } ${
        bubblePosition === 'top' ? 'flex-col-reverse' : ''
      } ${
        bubblePosition === 'bottom' ? 'flex-col' : ''
      }`}
    >
      <HortonAvatar {...avatarProps} />
      
      <div style={bubbleStyle}>
        {message}
        {/* Bubble tail */}
        <div
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            [bubblePosition === 'right' ? 'left' : 'right']: isHorizontal ? '-8px' : '20px',
            [bubblePosition === 'bottom' ? 'top' : 'bottom']: isHorizontal ? '50%' : '-8px',
            transform: isHorizontal ? 'translateY(-50%)' : 'translateX(-50%)',
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            [bubblePosition === 'right' ? 'borderRight' : 'borderLeft']: isHorizontal ? `10px solid ${bgColor}` : 'none',
            [bubblePosition === 'bottom' ? 'borderBottom' : 'borderTop']: !isHorizontal ? `10px solid ${bgColor}` : 'none',
          }}
        />
      </div>
    </div>
  )
}

// Quick greeting component
export const HortonGreeting: React.FC<{ userName?: string }> = ({ userName }) => {
  const { isDiary } = useTheme()
  
  const hour = new Date().getHours()
  let greeting = 'Hello'
  let mood: HortonMood = 'happy'
  
  if (hour < 6) {
    greeting = 'Still up?'
    mood = 'sleeping'
  } else if (hour < 12) {
    greeting = 'Good morning'
    mood = 'encouraging'
  } else if (hour < 17) {
    greeting = 'Good afternoon'
    mood = 'working'
  } else if (hour < 21) {
    greeting = 'Good evening'
    mood = 'happy'
  } else {
    greeting = 'Winding down?'
    mood = 'thinking'
  }

  const message = userName ? `${greeting}, ${userName}!` : `${greeting}!`

  if (!isDiary) {
    return (
      <div className="flex items-center gap-2">
        <span>🐘</span>
        <span className="text-secondary">{message}</span>
      </div>
    )
  }

  return <HortonWithBubble mood={mood} size="md" style="sticker" message={message} />
}

export default HortonAvatar
